/*
  OK FBI — Slice 3: SiteTicker + AnnouncementsPage + Typography.

  SiteTicker fetches active announcements from /api/announcements/active
  and renders them as a top ticker. Text is rendered as text (no
  dangerouslySetInnerHTML anywhere) so HTML/script injection is impossible.
*/
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2, Save, RefreshCw } from "lucide-react";

// Font id → CSS family (must match backend/announcements.py FONT_LIBRARY)
const FONT_CSS = {
  cairo:       "'Cairo', system-ui, sans-serif",
  tajawal:     "'Tajawal', system-ui, sans-serif",
  plex_arabic: "'IBM Plex Sans Arabic', system-ui, sans-serif",
  amiri:       "'Amiri', serif",
  almarai:     "'Almarai', system-ui, sans-serif",
  reem_kufi:   "'Reem Kufi', system-ui, sans-serif",
};

// ── Public ticker (mounted globally on the site shell) ───────────
export function SiteTicker({ scope = "site", pollMs = 30_000 }) {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/announcements/active?scope=${scope}`);
      setItems(data.announcements || []);
    } catch { /* silent */ }
  }, [scope]);
  useEffect(() => { load(); const t = setInterval(load, pollMs); return () => clearInterval(t); }, [load, pollMs]);
  if (!items.length) return null;
  // Show the highest-priority announcement first; if multiple, they scroll
  // sequentially in one bar with a middle-dot separator.
  const combined = items.map(a => a.text).join("   ·   ");
  const top = items[0];
  const speed = Math.max(5, Math.min(300, top.speed_seconds || 30));
  return (
    <div className="w-full overflow-hidden border-b border-neutral-900 sticky top-0 z-[60]"
         data-testid="site-ticker"
         dir={top.direction || "rtl"}
         style={{
           background: top.background || "#111827",
           color: top.color || "#f6e58a",
           fontFamily: FONT_CSS[top.font_id] || FONT_CSS.cairo,
           fontSize: `${top.font_size_px || 16}px`,
         }}>
      <div className="whitespace-nowrap py-1.5 px-4"
           style={ top.mode === "scroll"
             ? { animation: `okfbi-ticker ${speed}s linear infinite` }
             : {} }>
        {top.link_url ? (
          <a href={top.link_url} className="hover:underline" rel="noopener noreferrer" target="_blank">
            {combined}
          </a>
        ) : combined}
      </div>
      <style>{`
        @keyframes okfbi-ticker {
          0%   { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>
    </div>
  );
}


// ── OWNER page: /host/announcements ──────────────────────────────
function Row({ item, fonts, onChange, onDelete }) {
  const [d, setD] = useState(item);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setD(item); setDirty(false); }, [item]);
  const p = (k, v) => { setD(x => ({ ...x, [k]: v })); setDirty(true); };
  const save = async () => {
    try {
      const { data } = await api.patch(`/owner/announcements/${item.id}`, d);
      onChange(data); setDirty(false); toast.success("تم الحفظ");
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل الحفظ"); }
  };
  return (
    <div className="border border-neutral-900 rounded p-3 space-y-2 bg-neutral-950/50"
         data-testid={`announcement-${item.id}`}>
      <div className="flex items-center gap-2">
        <button onClick={()=>p("enabled", !d.enabled)}
                className={`text-[11px] font-cairo px-2 py-1 rounded border ${
                  d.enabled ? "bg-emerald-950/40 border-emerald-800 text-emerald-200"
                            : "bg-neutral-900 border-neutral-800 text-neutral-400"
                }`}>
          {d.enabled ? "مفعّل" : "متوقّف"}
        </button>
        <input value={d.title || ""} placeholder="عنوان اختياري"
               onChange={e=>p("title", e.target.value)}
               className="flex-1 bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-xs font-cairo"/>
        <select value={d.scope || "site"} onChange={e=>p("scope", e.target.value)}
                className="bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-xs font-cairo">
          <option value="site">الموقع</option>
          <option value="broadcast">البث</option>
        </select>
      </div>
      <textarea value={d.text || ""} rows={2} placeholder="النص"
                data-testid={`ann-text-${item.id}`}
                onChange={e=>p("text", e.target.value)}
                className="w-full bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-sm font-cairo"/>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <label className="text-[10px] font-cairo text-neutral-500">
          الوضع
          <select value={d.mode || "scroll"} onChange={e=>p("mode", e.target.value)}
                  className="w-full mt-0.5 bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-xs font-cairo">
            <option value="scroll">تمرير</option>
            <option value="static">ثابت</option>
          </select>
        </label>
        <label className="text-[10px] font-cairo text-neutral-500">
          سرعة (ث)
          <input type="number" min="5" max="300" value={d.speed_seconds || 30}
                 onChange={e=>p("speed_seconds", parseFloat(e.target.value))}
                 className="w-full mt-0.5 bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-xs font-mono"/>
        </label>
        <label className="text-[10px] font-cairo text-neutral-500">
          الخط
          <select value={d.font_id || "cairo"} onChange={e=>p("font_id", e.target.value)}
                  className="w-full mt-0.5 bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-xs font-cairo">
            {fonts.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
        </label>
        <label className="text-[10px] font-cairo text-neutral-500">
          حجم الخط
          <input type="number" min="10" max="48" value={d.font_size_px || 16}
                 onChange={e=>p("font_size_px", parseInt(e.target.value, 10))}
                 className="w-full mt-0.5 bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-xs font-mono"/>
        </label>
        <label className="text-[10px] font-cairo text-neutral-500">
          اللون
          <input type="color" value={d.color || "#f6e58a"} onChange={e=>p("color", e.target.value)}
                 className="w-full mt-0.5 bg-neutral-950 border border-neutral-800 rounded h-8"/>
        </label>
        <label className="text-[10px] font-cairo text-neutral-500">
          الخلفية
          <input type="color" value={d.background || "#111827"} onChange={e=>p("background", e.target.value)}
                 className="w-full mt-0.5 bg-neutral-950 border border-neutral-800 rounded h-8"/>
        </label>
        <label className="text-[10px] font-cairo text-neutral-500">
          يبدأ
          <input type="datetime-local" value={(d.start_at || "").slice(0,16)}
                 onChange={e=>p("start_at", e.target.value ? new Date(e.target.value).toISOString() : null)}
                 className="w-full mt-0.5 bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-xs font-mono"/>
        </label>
        <label className="text-[10px] font-cairo text-neutral-500">
          ينتهي
          <input type="datetime-local" value={(d.end_at || "").slice(0,16)}
                 onChange={e=>p("end_at", e.target.value ? new Date(e.target.value).toISOString() : null)}
                 className="w-full mt-0.5 bg-neutral-950 border border-neutral-800 rounded px-2 py-1 text-xs font-mono"/>
        </label>
      </div>
      {/* Preview */}
      <div className="rounded overflow-hidden border border-neutral-800"
           style={{ background: d.background || "#111827", color: d.color || "#f6e58a",
                    fontFamily: FONT_CSS[d.font_id] || FONT_CSS.cairo,
                    fontSize: `${d.font_size_px || 16}px` }}
           data-testid={`ann-preview-${item.id}`}>
        <div className="whitespace-nowrap py-1.5 px-3 truncate">{d.text || "…"}</div>
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={!dirty}
                className={`text-[11px] font-cairo px-2 py-1 rounded border inline-flex items-center gap-1 ${
                  dirty ? "bg-yellow-900/40 border-yellow-800 text-yellow-100"
                        : "border-neutral-800 text-neutral-600"
                }`}>
          <Save size={11}/> حفظ
        </button>
        <button onClick={()=>onDelete(item.id)}
                data-testid={`ann-delete-${item.id}`}
                className="text-[11px] font-cairo px-2 py-1 rounded border border-red-900/60 text-red-300 bg-red-950/30 inline-flex items-center gap-1">
          <Trash2 size={11}/> حذف
        </button>
      </div>
    </div>
  );
}


export default function AnnouncementsPage() {
  const [items, setItems] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [globalFont, setGlobalFont] = useState("cairo");
  const [newText, setNewText] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [a, t] = await Promise.all([
        api.get("/owner/announcements"),
        api.get("/typography"),
      ]);
      setItems(a.data.announcements || []);
      setFonts(a.data.fonts || t.data.fonts || []);
      setGlobalFont(t.data.global.font_id);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { document.title = "OK FBI · الإعلانات والخطوط"; load(); }, [load]);

  const create = async () => {
    if (!newText.trim()) { toast.error("النص مطلوب"); return; }
    try {
      await api.post("/owner/announcements", { text: newText.trim() });
      setNewText(""); toast.success("تم الإنشاء"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل"); }
  };

  const del = async (id) => {
    if (!window.confirm("حذف الإعلان؟")) return;
    try { await api.delete(`/owner/announcements/${id}`); load(); toast.success("تم الحذف"); }
    catch { toast.error("فشل الحذف"); }
  };

  const setFont = async (id) => {
    try {
      await api.patch("/owner/typography", { font_id: id });
      setGlobalFont(id); toast.success("تم تحديث الخط العام");
    } catch { toast.error("فشل"); }
  };

  return (
    <Shell>
      <div className="min-h-screen p-6 max-w-[1200px] mx-auto" dir="rtl"
           data-testid="announcements-page">
        <header className="mb-6">
          <div className="text-xs tracking-[0.35em] text-yellow-500/70 font-cairo">
            OK · FBI · ANNOUNCEMENTS & TYPOGRAPHY
          </div>
          <h1 className="text-3xl font-cairo text-yellow-400 font-black mt-1 flex items-center gap-2">
            <Megaphone size={22}/> الإعلانات والخطوط
          </h1>
        </header>

        {/* Global typography */}
        <div className="border border-neutral-900 rounded p-4 bg-neutral-950/60 mb-6">
          <div className="text-[10px] tracking-[0.3em] text-yellow-500/70 font-mono mb-2">GLOBAL TYPOGRAPHY</div>
          <div className="flex flex-wrap gap-2">
            {fonts.map(f => (
              <button key={f.id} onClick={()=>setFont(f.id)}
                      data-testid={`font-choice-${f.id}`}
                      className={`text-xs px-3 py-1.5 rounded border transition ${
                        globalFont === f.id
                          ? "bg-yellow-900/50 border-yellow-700 text-yellow-100"
                          : "border-neutral-800 text-neutral-300 hover:bg-neutral-900"
                      }`}
                      style={{ fontFamily: FONT_CSS[f.id] || undefined }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Quick create */}
        <div className="border border-yellow-800/40 rounded p-4 bg-neutral-950/60 mb-4">
          <div className="text-[10px] tracking-[0.3em] text-yellow-500/70 font-mono mb-2">NEW ANNOUNCEMENT</div>
          <div className="flex gap-2">
            <input value={newText} onChange={e=>setNewText(e.target.value)}
                   data-testid="new-ann-text"
                   placeholder="نص الإعلان (يُحفظ كنص خام — بلا HTML)"
                   className="flex-1 bg-neutral-950 border border-neutral-800 rounded px-3 py-2 text-sm font-cairo"/>
            <button onClick={create}
                    data-testid="new-ann-create"
                    className="px-3 py-2 bg-yellow-900/50 border border-yellow-800 text-yellow-100 rounded text-xs font-cairo inline-flex items-center gap-2">
              <Plus size={14}/> إنشاء
            </button>
            <button onClick={load}
                    className="px-2 py-2 border border-neutral-800 text-neutral-300 rounded">
              <RefreshCw size={14}/>
            </button>
          </div>
        </div>

        {loading ? <div className="text-sm text-neutral-500 font-cairo">جارِ التحميل…</div>
         : items.length === 0 ? <div className="text-sm text-neutral-500 font-cairo">لا توجد إعلانات بعد.</div>
         : <div className="space-y-3" data-testid="ann-list">
             {items.map(x => <Row key={x.id} item={x} fonts={fonts}
                                  onChange={u => setItems(prev => prev.map(y => y.id === u.id ? u : y))}
                                  onDelete={del}/>)}
           </div>}
      </div>
    </Shell>
  );
}
